/**
 * JiraSyncAdapter - Sync adapter for Jira
 * 
 * CHANGE HISTORY:
 * - 251026_0655 [pending] danielbeer: Created Jira sync adapter
 */

import { BaseSyncAdapter } from './BaseSyncAdapter.js';
import type { SyncChange } from '../types.js';

export interface JiraConfig {
  host: string;
  email: string;
  apiToken: string;
  projectKey: string;
}

export class JiraSyncAdapter extends BaseSyncAdapter {
  private config: JiraConfig;
  private baseUrl: string;

  constructor(config: JiraConfig) {
    super({
      name: 'jira',
      direction: 'both',
      entityTypes: ['project', 'task'],
    });

    this.config = config;
    this.baseUrl = `https://${config.host}/rest/api/3`;
  }

  /**
   * Pull changes from Jira
   */
  async pull(since: number): Promise<SyncChange[]> {
    const changes: SyncChange[] = [];

    try {
      // Convert timestamp to JQL date format
      const sinceDate = new Date(since).toISOString().split('T')[0];

      // Query Jira for updated issues
      const jql = `project = ${this.config.projectKey} AND updated >= "${sinceDate}"`;
      const response = await this.jiraRequest('GET', `/search?jql=${encodeURIComponent(jql)}`);

      for (const issue of response.issues || []) {
        changes.push({
          id: issue.key,
          type: 'update',
          entityType: 'task',
          data: this.mapJiraIssueToTask(issue),
          _modified: new Date(issue.fields.updated).getTime(),
        });
      }

      return changes;
    } catch (error) {
      console.error('Failed to pull from Jira:', error);
      throw error;
    }
  }

  /**
   * Push changes to Jira
   */
  async push(change: SyncChange): Promise<boolean> {
    try {
      if (change.type === 'create') {
        await this.createJiraIssue(change.data);
      } else if (change.type === 'update') {
        await this.updateJiraIssue(change.id, change.data);
      } else if (change.type === 'delete') {
        await this.deleteJiraIssue(change.id);
      }

      return true;
    } catch (error) {
      console.error('Failed to push to Jira:', error);
      return false;
    }
  }

  /**
   * Create Jira issue
   */
  private async createJiraIssue(task): Promise<any> {
    const issue = {
      fields: {
        project: { key: this.config.projectKey },
        summary: task.name,
        description: task.description || '',
        issuetype: { name: this.mapTaskTypeToJira(task.type) },
        priority: { name: this.mapPriorityToJira(task.priority) },
      },
    };

    return await this.jiraRequest('POST', '/issue', issue);
  }

  /**
   * Update Jira issue
   */
  private async updateJiraIssue(issueKey: string, task): Promise<any> {
    const update = {
      fields: {
        summary: task.name,
        description: task.description || '',
        priority: { name: this.mapPriorityToJira(task.priority) },
      },
    };

    return await this.jiraRequest('PUT', `/issue/${issueKey}`, update);
  }

  /**
   * Delete Jira issue
   */
  private async deleteJiraIssue(issueKey: string): Promise<any> {
    return await this.jiraRequest('DELETE', `/issue/${issueKey}`);
  }

  /**
   * Make Jira API request
   */
  private async jiraRequest(method: string, endpoint: string, body?: any): Promise<any> {
    const auth = Buffer.from(`${this.config.email}:${this.config.apiToken}`).toString('base64');

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method,
      headers: {
        'Authorization': `Basic ${auth}`,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new Error(`Jira API error: ${response.statusText}`);
    }

    if (method === 'DELETE') {
      return {};
    }

    return await response.json();
  }

  /**
   * Map Jira issue to internal task format
   */
  private mapJiraIssueToTask(issue): any {
    return {
      id: issue.key,
      name: issue.fields.summary,
      description: issue.fields.description || '',
      type: this.mapJiraTypeToTask(issue.fields.issuetype.name),
      status: this.mapJiraStatusToTask(issue.fields.status.name),
      priority: this.mapJiraPriorityToTask(issue.fields.priority?.name || 'Medium'),
      assigneeId: issue.fields.assignee?.accountId,
      reporterId: issue.fields.reporter?.accountId,
      createdAt: issue.fields.created,
      updatedAt: issue.fields.updated,
      externalId: issue.key,
      externalSource: 'jira',
    };
  }

  /**
   * Map task type to Jira issue type
   */
  private mapTaskTypeToJira(type: string): string {
    const mapping: Record<string, string> = {
      feature: 'Story',
      bug: 'Bug',
      improvement: 'Improvement',
      documentation: 'Task',
      research: 'Task',
      other: 'Task',
    };
    return mapping[type] || 'Task';
  }

  /**
   * Map Jira issue type to task type
   */
  private mapJiraTypeToTask(type: string): string {
    const mapping: Record<string, string> = {
      Story: 'feature',
      Bug: 'bug',
      Improvement: 'improvement',
      Task: 'other',
    };
    return mapping[type] || 'other';
  }

  /**
   * Map priority to Jira priority
   */
  private mapPriorityToJira(priority: string): string {
    const mapping: Record<string, string> = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      critical: 'Highest',
    };
    return mapping[priority] || 'Medium';
  }

  /**
   * Map Jira priority to internal priority
   */
  private mapJiraPriorityToTask(priority: string): string {
    const mapping: Record<string, string> = {
      Lowest: 'low',
      Low: 'low',
      Medium: 'medium',
      High: 'high',
      Highest: 'critical',
    };
    return mapping[priority] || 'medium';
  }

  /**
   * Map Jira status to task status
   */
  private mapJiraStatusToTask(status: string): string {
    const mapping: Record<string, string> = {
      'To Do': 'todo',
      'In Progress': 'in-progress',
      'In Review': 'review',
      'Blocked': 'blocked',
      'Done': 'done',
      'Cancelled': 'cancelled',
    };
    return mapping[status] || 'todo';
  }
}

export default JiraSyncAdapter;
